/** Streaming relay for the browser version and the pass the account server signed for it. */
export interface RelayConfig {
  base: string;
  ticket: string;
}

let relay: RelayConfig | null = null;
/** Trial pool: the pass for the line assigned to the current stream (player only). */
let streamTicket: string | null = null;

/** Set from the account session (main thread) and passed on to the catalog worker. */
export function setRelay(next: RelayConfig | null): void {
  // A new relay address invalidates image URLs pointing at the old one.
  if (next?.base !== relay?.base) imagePass = null;
  relay = next;
}

/** Pass used for images, kept for a while so image URLs (and the browser's cache of them) stay stable. */
let imagePass: { ticket: string; since: number } | null = null;
const IMAGE_PASS_MS = 20 * 60 * 1000;

/**
 * Logos and posters: an https page can't show http images, so in the
 * browser version those go through the relay. Other images load directly.
 */
export function imageUrl(src: string | null): string | null {
  if (!src || !relay || !src.startsWith('http:') || typeof location === 'undefined' || location.protocol !== 'https:') return src;
  if (!imagePass || Date.now() - imagePass.since > IMAGE_PASS_MS) imagePass = { ticket: relay.ticket, since: Date.now() };
  return `${relay.base}/fetch?t=${encodeURIComponent(imagePass.ticket)}&url=${encodeURIComponent(src)}`;
}

export function setStreamTicket(ticket: string | null): void {
  streamTicket = ticket;
}

/** The relay and pass to use now, or null when requests don't go through the relay. */
export function currentRelay(): RelayConfig | null {
  return relay ? { base: relay.base, ticket: streamTicket ?? relay.ticket } : null;
}

/**
 * Request URL used for provider traffic (playlists, APIs, streams). Browsers
 * enforce CORS, which IPTV servers rarely allow; in development requests go
 * through the Vite dev proxy, in the desktop app through the Electron main
 * process, in the iOS app through its native relay (ProviderRelay.swift), in
 * the browser version through the streaming relay (apps/relay). Packaged TV
 * apps are not subject to CORS and use URLs unchanged.
 */
export function providerUrl(url: string): string {
  // Already an app-internal URL (desktop audio conversion, probes).
  if (url.startsWith('viva-provider:')) return url;
  // Desktop app (Electron main process) and iOS app (native relay) fetch provider URLs.
  if (typeof location !== 'undefined' && (location.protocol === 'app:' || location.protocol === 'capacitor:')) {
    return `viva-provider://fetch/?url=${encodeURIComponent(url)}`;
  }
  const via = currentRelay();
  if (via) {
    // Relay endpoints (conversions) are already relay URLs. A stream's pass lasts minutes, and a
    // reconnect can come long after the URL was made: give it the current pass.
    if (url.startsWith(`${via.base}/`)) return withPass(url, via.ticket);
    return `${via.base}/fetch?t=${encodeURIComponent(via.ticket)}&url=${encodeURIComponent(url)}`;
  }
  return import.meta.env.DEV ? `/__proxy?url=${encodeURIComponent(url)}` : url;
}

/** A relay URL with its pass (`t`) replaced by `ticket`. */
function withPass(url: string, ticket: string): string {
  const next = new URL(url);
  if (!next.searchParams.has('t') || next.searchParams.get('t') === ticket) return url;
  next.searchParams.set('t', ticket);
  return next.toString();
}
